import { useEffect, useState } from 'react';
import type { PokerLegal, PokerView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Coins } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { MySeat, SeatView, useNarrow, useSpeech } from './common';

const CATEGORY: Record<string, string> = {
  HIGH_CARD: 'High card',
  PAIR: 'Pair',
  TWO_PAIR: 'Two pair',
  THREE_OF_A_KIND: 'Three of a kind',
  STRAIGHT: 'Straight',
  FLUSH: 'Flush',
  FULL_HOUSE: 'Full house',
  FOUR_OF_A_KIND: 'Four of a kind',
  STRAIGHT_FLUSH: 'Straight flush',
};

export function PokerTable() {
  const t = useT();
  const view = useGame((s) => s.view) as PokerView;
  const legal = useGame((s) => s.legal) as PokerLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const total = view.players.length;
  const me = mySeat !== null ? view.players[mySeat] : null;
  const canRaise = legal?.minRaiseTo !== null && legal?.minRaiseTo !== undefined && legal.maxRaiseTo !== null;
  const [raiseTo, setRaiseTo] = useState(0);
  useEffect(() => {
    if (canRaise) setRaiseTo(legal!.minRaiseTo!);
  }, [canRaise, legal?.minRaiseTo]); // eslint-disable-line react-hooks/exhaustive-deps
  const revealed = view.lastResult?.revealed ?? {};
  const winners = new Set((view.lastResult?.pots ?? []).flatMap((p) => p.winners));
  // Always within the legal range (the stored value can be stale after a reconnect or a new street).
  const raiseValue = canRaise ? Math.min(legal!.maxRaiseTo!, Math.max(legal!.minRaiseTo!, raiseTo)) : 0;
  const preset = (to: number) => setRaiseTo(Math.max(legal!.minRaiseTo!, Math.min(legal!.maxRaiseTo!, Math.round(to))));
  const isBet = view.currentBet === 0;
  const active = !!legal && legal.canFold;
  const narrow = useNarrow();
  const boardSize = narrow ? 'md' : 'lg';

  return (
    <>
      <div className="stage">
      <div className="felt" />
      {view.players.map((p, s) => (
        <SeatView
          key={s}
          seat={s}
          info={seats[s]}
          mySeat={mySeat}
          total={total}
          turn={turn}
          speech={speech[s]}
          folded={p?.status === 'FOLDED' || p?.status === 'SITTING_OUT' || p?.status === 'BUSTED'}
          sub={p ? <Coins amount={p.chips} size={14} /> : null}
          tags={
            p && (
              <>
                {view.button === s && <span className="tag">D</span>}
                {view.phase !== 'WAITING' && view.phase !== 'HAND_END' && view.sbSeat === s && <span className="tag">SB</span>}
                {view.phase !== 'WAITING' && view.phase !== 'HAND_END' && view.bbSeat === s && <span className="tag">BB</span>}
                {p.status === 'ALLIN' && <span className="tag blind">ALL-IN</span>}
                {p.status === 'FOLDED' && <span className="tag folded">FOLD</span>}
                {winners.has(s) && view.phase === 'HAND_END' && <span className="tag seen">WIN</span>}
              </>
            )
          }
        >
          {p && (revealed[s] ? (
            <div className="shown-cards" aria-label="Shown cards">
              {revealed[s]!.map((c) => (
                <PlayingCard key={c} id={c} size="md" />
              ))}
            </div>
          ) : p.hasCards ? (
            <div className="mini-cards">
              {Array.from({ length: view.config.variant === 'PLO' ? 4 : 2 }, (_, i) => (
                <PlayingCard key={i} faceDown size="sm" />
              ))}
            </div>
          ) : null)}
          {p && p.bet > 0 && (
            <span className="bet">
              <Coins amount={p.bet} size={12} />
            </span>
          )}
          {view.phase === 'HAND_END' && view.lastResult?.categories[s] && <span className="tag">{CATEGORY[view.lastResult.categories[s]!]}</span>}
        </SeatView>
      ))}
      <div className="felt-center centered">
        {view.phase === 'WAITING' ? (
          <div className="center-note">{t('table.waitingPlayers')}</div>
        ) : (
          <>
            <div className="board" aria-label="Board">
              {(view.phase === 'HAND_END' ? (view.lastResult?.board ?? view.board) : view.board).map((c, i) => (
                <PlayingCard key={c} id={c} size={boardSize} dealIndex={i} />
              ))}
              {Array.from({ length: Math.max(0, 5 - (view.phase === 'HAND_END' ? (view.lastResult?.board ?? view.board) : view.board).length) }, (_, i) => (
                <span key={`e${i}`} className={`pcard ${boardSize}`} style={{ border: '2px dashed rgba(255,255,255,0.2)', boxShadow: 'none' }} aria-hidden />
              ))}
            </div>
            <div className="pot">
              <span className="pot-label">{t('table.pot')}</span>
              <Coins amount={view.phase === 'HAND_END' ? (view.lastResult?.pots.reduce((a, p) => a + p.amount, 0) ?? 0) : view.pot} size={20} />
            </div>
            <div className="center-note">
              Blinds {view.blinds.sb}/{view.blinds.bb} · {t('table.hand', { n: view.handNo })}
            </div>
          </>
        )}
      </div>
      <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={me ? <Coins amount={me.chips} size={14} /> : null} />
      </div>
      <div className="table-bottom">
        <div className="my-cards poker-hole" aria-label="Your hole cards">
          {view.hand.map((c, i) => (
            <PlayingCard key={c} id={c} size={view.hand.length > 2 ? 'md' : 'lg'} dealIndex={i} unplayable={me?.status === 'FOLDED'} />
          ))}
        </div>
        {/* The controls always occupy their space (hidden when it is not my turn) so the table never resizes. */}
        <div className="poker-controls" aria-hidden={!active} data-active={active}>
          <div className="raise-panel">
            {active && canRaise && (
              <>
                <input type="range" min={legal!.minRaiseTo!} max={legal!.maxRaiseTo!} step={Math.max(1, view.blinds.bb / 2)} value={raiseValue} onChange={(e) => setRaiseTo(Number(e.target.value))} aria-label={`${isBet ? 'Bet' : t('pk.raiseTo', { n: '' })} ${raiseValue}`} />
                <div className="row" style={{ gap: 6, justifyContent: 'center' }}>
                  <button className="pill" onClick={() => preset(legal!.minRaiseTo!)}>{t('pk.min')}</button>
                  <button className="pill" onClick={() => preset(view.currentBet + view.pot / 2)}>{t('pk.halfPot')}</button>
                  <button className="pill" onClick={() => preset(view.currentBet + view.pot)}>{t('pk.pot')}</button>
                  {legal!.allInTo !== null && <button className="pill gold" onClick={() => preset(legal!.allInTo!)}>{t('pk.allIn')}</button>}
                </div>
              </>
            )}
          </div>
          <div className="action-bar">
            {active && (
              <>
                <button className="btn danger" disabled={acting} onClick={() => void act({ type: 'fold' })}>
                  {t('pk.fold')}
                </button>
                {legal!.canCheck ? (
                  <button className="btn" disabled={acting} onClick={() => void act({ type: 'check' })}>
                    {t('pk.check')}
                  </button>
                ) : (
                  legal!.call !== null && (
                    <button className="btn primary" disabled={acting} onClick={() => void act({ type: 'call' })}>
                      {t('pk.call', { n: legal!.call })}
                    </button>
                  )
                )}
                {canRaise && (
                  <button className="btn gold" disabled={acting} onClick={() => void act(legal!.allInTo !== null && raiseValue >= legal!.allInTo ? { type: 'allin' } : { type: 'raise', to: raiseValue })}>
                    {legal!.allInTo !== null && raiseValue >= legal!.allInTo ? t('pk.allIn') : isBet ? t('pk.bet', { n: raiseValue }) : t('pk.raiseTo', { n: raiseValue })}
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
